import type { GTooltipCardData } from '~/presentation/ui';

import { IPAMetricKey } from '../types/chart-tooltip';

type IPAMetricKeyValue = Record<IPAMetricKey, GTooltipCardData>;

export const PA_METRIC_TOOLTIP: IPAMetricKeyValue = {
  [IPAMetricKey.PAGE_SESSIONS]: {
    title: 'Sessions',
    content: 'A period during which a visitor interacts with your online store',
  },
  [IPAMetricKey.PAGE_VISITORS]: {
    title: 'Visitors',
    content: 'Number of unique individuals who visit your online store',
  },
  [IPAMetricKey.PAGE_BR]: {
    title: 'Bounce rate',
    content:
      'Percentage of visitors who leave your store after viewing only one page, without interacting further',
    formula: (
      <>
        <span className="formula-variable">Bounce rate</span>
        <span> = </span>
        <span className="formula-input">sessions with a pageview</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total sessions</span>
      </>
    ),
  },
  [IPAMetricKey.PAGE_CTR]: {
    title: 'Click-through rate',
    content:
      'Percentage of clicks to open new page, compared to the number of times the page was viewed',
    formula: (
      <>
        <span className="formula-variable">Click-through rate</span>
        <span> = </span>
        <span className="formula-input">total clicks</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total pageviews</span>
      </>
    ),
  },
  [IPAMetricKey.PAGE_AVG_TIME]: {
    title: 'Average time on page',
    content: 'Average duration that visitors spend on a page before clicking to open new page',
    formula: (
      <>
        <span className="formula-variable">Average time on page</span>
        <span> = </span>
        <span className="formula-input">total time on page</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">(total pageviews - total exits)</span>
      </>
    ),
  },
  [IPAMetricKey.PAGE_VIEWS]: {
    title: 'Pageviews',
    content: 'Number of times a page on your online store has been viewed by visitors',
  },
  [IPAMetricKey.PAGE_RPV]: {
    title: 'Revenue per visitor',
    content: 'Average revenue generated from each visitor',
    formula: (
      <>
        <span className="formula-variable">Revenue per visitor</span>
        <span> = </span>
        <span className="formula-input">total revenue</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total visitors</span>
      </>
    ),
  },
  [IPAMetricKey.PAGE_SESSIONS_BY_VISITOR]: {
    title: 'Sessions by visitor type',
    content: `<p>Numbers of new and returning visitors</p>
      <ul>
        <li>
          <span>New visitor:</span>
          <span> who accesses your store for the first time after GemX installation</span>
        </li>
        <li>
          <span>Returning visitor:</span>
          <span> who comes back to your store after GemX installation</span>
        </li>
      </ul>`,
  },
  [IPAMetricKey.PAGE_SESSIONS_BY_DEVICE]: {
    title: 'Sessions by device type',
    content: `Sessions in your online store by the visitor's device type`,
  },
  [IPAMetricKey.PAGE_SESSIONS_BY_TRAFFIC]: {
    title: 'Sessions by traffic source',
    content: 'Sessions on your page by where visitors come from',
  },
  [IPAMetricKey.PAGE_PATH_ANALYSIS]: {
    title: 'Journey analysis',
    content:
      'Journey analysis tracks the journey from entry to purchase and shows where visitors leave the store',
  },
  [IPAMetricKey.PAGE_ADDED_TO_CART]: {
    title: 'Added to cart',
    content:
      'Total number of the event when a customer adds a product to their cart on your online store',
  },
  [IPAMetricKey.PAGE_REACHED_CHECKOUT]: {
    title: 'Sessions that reached checkout',
    content: 'Sessions in your online store in which the checkout page was reached',
  },
  [IPAMetricKey.PAGE_COMPLETE_CHECKOUT]: {
    title: 'Sessions that completed checkout',
    content: 'Sessions in your online store in which a purchase was completed',
  },
  [IPAMetricKey.PAGE_ORDERS]: {
    title: 'Orders',
    content: 'Number of orders that went through this pages',
  },
  [IPAMetricKey.PAGE_CR]: {
    title: 'Conversion rate',
    content: 'Percentage of online store sessions that completed an order',
    formula: (
      <>
        <span className="formula-variable">Conversion rate</span>
        <span> = </span>
        <span className="formula-input">sessions that completed checkout</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total sessions</span>
      </>
    ),
  },
  [IPAMetricKey.PAGE_AOV]: {
    title: 'Average order value',
    content: 'Average value of orders placed in your online store',
    formula: (
      <>
        <span className="formula-variable">Average order value</span>
        <span> = </span>
        <span className="formula-input">total revenue</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total orders</span>
      </>
    ),
  },
  [IPAMetricKey.PAGE_REVENUE]: {
    title: 'Revenue',
    content: 'Total income generated from sales after discounts',
    formula: (
      <>
        <span className="formula-variable">Revenue</span>
        <span> = </span>
        <span className="formula-input">gross sales</span>
        <span className="formula-operator"> - </span>
        <span className="formula-input">discounts</span>
      </>
    ),
  },
  [IPAMetricKey.PAGE_ADD_TO_CART_RATE]: {
    title: 'Add to cart rate',
    content: 'Percentage of sessions in which the customer added item to the cart',
    formula: (
      <>
        <span className="formula-variable">Added to cart rate</span>
        <span> = </span>
        <span className="formula-input">sessions with cart additions</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total sessions</span>
      </>
    ),
  },
  [IPAMetricKey.PAGE_CART_ADDITION]: {
    title: 'Sessions with cart additions',
    content: 'Sessions in your online store in which a visitor added item to the cart',
  },
  [IPAMetricKey.PAGE_NEW_RETURNING_CUSTOMER]: {
    title: 'New vs returning visitors',
    content: 'Numbers of first-time and returning customers.',
  },
};
